import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioButton, NioSection } from '../../../components';
import api from '../../../utils/api';
import CardPaymentForm from './CardPaymentForm';
import { useTranslation, STRIPE_LOCALES } from '../../../i18n/I18nProvider';

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function formatMoney(amount, currency) {
  const c = String(currency || 'EUR').toUpperCase();
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return c === 'TND' ? '0,000 TND' : '0,00 €';
  if (c === 'TND') return `${n.toFixed(3)} TND`;
  if (c === 'EUR') return `${n.toFixed(2).replace('.', ',')} €`;
  return `${n.toFixed(2)} ${c}`;
}

const PAYMENT_STYLES = `
  .payment-page-wrap {
    max-width: 1040px;
    margin: 0 auto;
  }
  .payment-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  @media (min-width: 992px) {
    .payment-layout {
      grid-template-columns: 1.15fr 0.85fr;
      align-items: start;
    }
  }
  .payment-panel {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.5rem 1.6rem;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  }
  .payment-panel-head {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1.35rem;
  }
  .payment-panel-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #f1f5f9;
    color: #0f172a;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .payment-panel-icon svg {
    width: 22px;
    height: 22px;
  }
  .payment-panel-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 0.2rem;
    line-height: 1.2;
  }
  .payment-panel-sub {
    color: #64748b;
    font-size: 0.92rem;
    margin: 0;
  }
  .payment-method-tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
  }
  .payment-method-tab {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    border-radius: 999px;
    padding: 0.35rem 0.9rem;
    font-size: 0.88rem;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .payment-method-tab.active {
    background: #0f172a;
    border-color: #0f172a;
    color: #fff;
  }
  .payment-method-tab:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .payment-summary-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 1.1rem;
  }
  .payment-summary-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.55rem 0;
    color: #334155;
    font-size: 0.95rem;
  }
  .payment-summary-row strong {
    color: #0f172a;
    font-weight: 600;
    text-align: right;
  }
  .payment-summary-row--muted span:first-child {
    color: #64748b;
  }
  .payment-summary-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;
    padding-top: 0.9rem;
    border-top: 1px solid #e5e7eb;
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
  }
  .payment-amount-display {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;
    padding: 0.85rem 1rem;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }
  .payment-amount-display span {
    font-size: 0.9rem;
    font-weight: 600;
    color: #475569;
  }
  .payment-amount-display strong {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
  }
  .payment-onsite-form .payment-field textarea {
    min-height: 88px;
    resize: vertical;
  }
  .payment-alert {
    border-radius: 8px;
    padding: 0.65rem 0.85rem;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
  .payment-alert--error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
  }
  .payment-field-hint {
    font-size: 0.78rem;
    color: #94a3b8;
    margin: 0.35rem 0 0;
  }
  .payment-alert--warning {
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
  }
  .payment-card-form .payment-field {
    margin-bottom: 1rem;
  }
  .payment-card-form .payment-field label {
    display: block;
    font-size: 0.88rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.4rem;
  }
  .payment-card-form .payment-field input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.62rem 0.75rem;
    font-size: 0.95rem;
    color: #0f172a;
    background: #fff;
  }
  .payment-card-form .payment-field input:focus {
    outline: none;
    border-color: #94a3b8;
    box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.2);
  }
  .payment-field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem;
  }
  .payment-brands {
    display: flex;
    gap: 0.45rem;
    margin: -0.35rem 0 0.85rem;
  }
  .payment-brands span {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #94a3b8;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.15rem 0.4rem;
    background: #f8fafc;
  }
  .payment-stripe-input {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.72rem 0.75rem;
    background: #fff;
    min-height: 46px;
    display: flex;
    align-items: center;
  }
  .payment-stripe-input--icon {
    position: relative;
    padding-right: 2.25rem;
  }
  .payment-stripe-input .StripeElement {
    width: 100%;
  }
  .payment-input-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: #94a3b8;
    pointer-events: none;
  }
  .payment-submit-btn {
    width: 100%;
    margin-top: 0.35rem;
    border: none;
    border-radius: 8px;
    background: #0f172a;
    color: #fff;
    font-weight: 600;
    font-size: 1rem;
    padding: 0.85rem 1rem;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .payment-submit-btn:hover:not(:disabled) {
    background: #1e293b;
  }
  .payment-submit-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .payment-secure-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    margin: 0.85rem 0 0;
    font-size: 0.78rem;
    color: #94a3b8;
    text-align: center;
  }
  .payment-secure-note svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
  .payment-onsite-form .payment-field {
    margin-bottom: 1rem;
  }
  .payment-onsite-form .payment-field label {
    display: block;
    font-size: 0.88rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.4rem;
  }
  .payment-onsite-form .payment-field input,
  .payment-onsite-form .payment-field textarea {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.62rem 0.75rem;
    font-size: 0.95rem;
    color: #0f172a;
  }
  .payment-back-link {
    margin-top: 1rem;
  }
  .payment-service-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    background: #eff6ff;
    color: #1e40af;
    padding: 0.7rem 0.9rem;
    font-size: 0.88rem;
    margin-bottom: 1.1rem;
  }
  .payment-service-banner svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
  .payment-service-banner-title {
    font-weight: 600;
    display: block;
    margin-bottom: 0.15rem;
  }
  .payment-service-banner-sub {
    opacity: 0.8;
    font-size: 0.82rem;
  }
  .payment-trust-row {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-bottom: 1.1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #f1f5f9;
  }
  .payment-trust-badge {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    padding: 0.2rem 0.6rem;
  }
  .payment-trust-badge svg {
    width: 12px;
    height: 12px;
    color: #16a34a;
    flex-shrink: 0;
  }
  .payment-alert--onsite {
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    border-radius: 8px;
    padding: 0.65rem 0.85rem;
    font-size: 0.88rem;
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    margin-bottom: 1rem;
  }
  .payment-alert--onsite svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: #d97706;
  }
  .payment-canceled-icon {
    margin-right: 0.4rem;
    vertical-align: -0.1em;
  }
`;

export default function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getStoredUser();
  const { t, language } = useTranslation();
  const stripeLocale = STRIPE_LOCALES[language] || 'fr';

  const initial = useMemo(() => {
    const state = location.state || {};
    const params = new URLSearchParams(location.search || '');
    return {
      amount: state.amount ?? params.get('amount') ?? '149',
      description: state.description ?? params.get('description') ?? 'Prestation de service',
      requestId: state.requestId ?? params.get('requestId') ?? '',
      supportTicketId: state.supportTicketId ?? params.get('supportTicketId') ?? '',
      canceled: params.get('canceled') === '1',
    };
  }, [location.state, location.search]);

  const payAmount = useMemo(() => {
    const n = Number(initial.amount);
    return Number.isFinite(n) && n > 0 ? n : 149;
  }, [initial.amount]);

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [description, setDescription] = useState(initial.description);
  const [onSiteNotes, setOnSiteNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeConfig, setStripeConfig] = useState({
    loaded: false,
    cardFormReady: false,
    stripeConfigured: false,
    missing: [],
  });
  const [currency, setCurrency] = useState('EUR');

  const resolvePublishableKey = (fromApi) => {
    const apiKey = String(fromApi || '').trim();
    if (/^pk_(test|live)_/.test(apiKey)) return apiKey;
    const envKey = String(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
    if (/^pk_(test|live)_/.test(envKey)) return envKey;
    return '';
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPaymentConfig();
        if (cancelled || !res?.success) return;
        const data = res.data || {};
        if (data.currency) setCurrency(String(data.currency).toUpperCase());
        setStripeConfig({
          loaded: true,
          cardFormReady: Boolean(data.cardFormReady),
          stripeConfigured: Boolean(data.stripeConfigured),
          missing: Array.isArray(data.missing) ? data.missing : [],
        });
        const pk = resolvePublishableKey(data.publishableKey);
        if (pk) setStripePromise(loadStripe(pk, { locale: stripeLocale }));
      } catch {
        const envKey = resolvePublishableKey();
        if (!cancelled && envKey) {
          setStripePromise(loadStripe(envKey, { locale: stripeLocale }));
          setStripeConfig({ loaded: true, cardFormReady: true, stripeConfigured: true, missing: [] });
        } else if (!cancelled) {
          setStripeConfig({ loaded: true, cardFormReady: false, stripeConfigured: false, missing: ['backend'] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stripeLocale]);

  useEffect(() => {
    if (!user) {
      navigate('/auth/login', { replace: true, state: { from: '/payment' } });
    }
  }, [user, navigate]);

  const numericAmount = payAmount;
  const formattedTotal = formatMoney(numericAmount, currency);
  const serviceLabel = description || t('payment.defaultDescription');

  const buildUserPayload = () => ({
    userId: user?.id || user?._id,
    userEmail: user?.email,
    userName: [user?.name, user?.lastName].filter(Boolean).join(' ') || user?.name,
    userPhone: user?.phone,
    requestId: initial.requestId || undefined,
    supportTicketId: initial.supportTicketId || undefined,
  });

  const handleCardRedirect = async () => {
    setError('');
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError(t('payment.invalidAmount'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.initPayment({
        amount: numericAmount,
        description: description || t('payment.paymentDescription'),
        ...buildUserPayload(),
      });
      if (res?.success && res.data?.payUrl) {
        localStorage.setItem('lastPaymentId', String(res.data.paymentId));
        window.location.href = res.data.payUrl;
        return;
      }
      setError(res?.message || t('payment.stripeRedirectError'));
    } catch (err) {
      setError(err.message || t('payment.stripeError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOnSiteSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError(t('payment.invalidAmount'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.createOnSitePayment({
        amount: numericAmount,
        description: description || t('payment.onSiteDescription'),
        notes: onSiteNotes || undefined,
        ...buildUserPayload(),
      });
      if (res?.success) {
        localStorage.setItem('lastPaymentId', String(res.data.paymentId));
        navigate(`/payment/return?paymentId=${res.data.paymentId}`);
        return;
      }
      setError(res?.message || t('payment.onSiteSaveError'));
    } catch (err) {
      setError(err.message || t('payment.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardSuccess = ({ paymentId }) => {
    localStorage.setItem('lastPaymentId', String(paymentId));
    navigate(`/payment/return?paymentId=${paymentId}`);
  };

  const renderPanelTitle = () => {
    if (paymentMethod === 'card') return t('payment.cardTitle');
    return t('payment.onSiteTitle');
  };

  const renderPanelSub = () => {
    if (paymentMethod === 'card') return t('payment.cardSub');
    return t('payment.onSiteSub');
  };

  const renderPaymentForm = () => {
    if (paymentMethod === 'card') return renderCardForm();
    return (
      <form className="payment-onsite-form" onSubmit={handleOnSiteSubmit}>
        <div className="payment-alert--onsite" role="note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('payment.onSiteWarning')}</span>
        </div>
        <div className="payment-field">
          <label htmlFor="onsite-notes">{t('payment.commentLabel')}</label>
          <textarea
            id="onsite-notes"
            value={onSiteNotes}
            onChange={(e) => setOnSiteNotes(e.target.value)}
            placeholder={t('payment.commentPlaceholder')}
            disabled={submitting}
          />
        </div>
        <button type="submit" className="payment-submit-btn" disabled={submitting}>
          {submitting ? t('payment.saving') : t('payment.confirm', { amount: formattedTotal })}
        </button>
      </form>
    );
  };

  const renderCardForm = () => {
    if (!stripePromise) {
      return (
        <div className="payment-alert payment-alert--warning">
          {!stripeConfig.loaded ? (
            t('payment.loadingStripe')
          ) : stripeConfig.missing.includes('backend') ? (
            t('payment.backendError')
          ) : (
            <>
              {t('payment.stripeKeyMissing')}
              {stripeConfig.stripeConfigured && (
                <div className="mt-2">
                  <button type="button" className="payment-submit-btn" onClick={handleCardRedirect} disabled={submitting}>
                    {submitting ? t('payment.redirecting') : t('payment.payStripe', { amount: formattedTotal })}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return (
      <Elements stripe={stripePromise} options={{ locale: stripeLocale }}>
        <CardPaymentForm
          amount={payAmount}
          currency={currency}
          formattedTotal={formattedTotal}
          description={description}
          user={user}
          requestId={initial.requestId}
          supportTicketId={initial.supportTicketId}
          onSuccess={handleCardSuccess}
          onError={setError}
          disabled={submitting}
        />
      </Elements>
    );
  };

  return (
    <AppLayout title={t('payment.title')} rootClass="layout-1">
      <style>{PAYMENT_STYLES}</style>
      <NioSection className="pt-120 pt-lg-160 pb-5">
        <NioSection.Content>
          <div className="payment-page-wrap">
            {initial.canceled && (
              <div className="payment-alert payment-alert--warning mb-3" role="alert">
                <svg className="payment-canceled-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t('payment.canceled')}
              </div>
            )}
            {error && <div className="payment-alert payment-alert--error">{error}</div>}

            <div className="payment-layout">
              <div className="payment-panel">
                <div className="payment-panel-head">
                  <div className="payment-panel-icon">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="payment-panel-title">{renderPanelTitle()}</h2>
                    <p className="payment-panel-sub">{renderPanelSub()}</p>
                  </div>
                </div>

                <div className="payment-amount-display">
                  <span>{t('payment.amountToPay')}</span>
                  <strong>{formattedTotal}</strong>
                </div>

                <div className="payment-method-tabs">
                  <button
                    type="button"
                    className={`payment-method-tab ${paymentMethod === 'card' ? 'active' : ''}`}
                    onClick={() => setPaymentMethod('card')}
                    disabled={submitting}
                  >
                    {t('payment.card')}
                  </button>
                  <button
                    type="button"
                    className={`payment-method-tab ${paymentMethod === 'on_site' ? 'active' : ''}`}
                    onClick={() => setPaymentMethod('on_site')}
                    disabled={submitting}
                  >
                    {t('payment.onSite')}
                  </button>
                </div>

                {renderPaymentForm()}

                <div className="payment-back-link">
                  <NioButton
                    type="button"
                    label={t('payment.back')}
                    className="btn-outline-secondary btn-sm"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="payment-panel">
                <h3 className="payment-summary-title">{t('payment.summaryTitle')}</h3>

                <div className="payment-service-banner">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                  <div>
                    <span className="payment-service-banner-title">{t('payment.serviceReminderBanner', { desc: serviceLabel })}</span>
                    {user?.email && (
                      <span className="payment-service-banner-sub">{t('payment.receiptNote', { email: user.email })}</span>
                    )}
                  </div>
                </div>

                <div className="payment-trust-row">
                  <span className="payment-trust-badge">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 3l8 4v5c0 5-3.5 9-8 9s-8-4-8-9V7l8-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                    {t('payment.trustEncrypted')}
                  </span>
                  <span className="payment-trust-badge">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t('payment.trustStripe')}
                  </span>
                  <span className="payment-trust-badge">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t('payment.trustReceipt')}
                  </span>
                </div>

                <div className="payment-summary-row">
                  <span>{t('payment.service')}</span>
                  <strong>{serviceLabel}</strong>
                </div>

                <div className="payment-summary-row">
                  <span>{t('payment.amountToPay')}</span>
                  <strong>{formattedTotal}</strong>
                </div>

                <div className="payment-summary-row payment-summary-row--muted">
                  <span>{t('payment.subtotal')}</span>
                  <strong>{formattedTotal}</strong>
                </div>
                <div className="payment-summary-row payment-summary-row--muted">
                  <span>{t('payment.vatIncluded')}</span>
                  <strong>{currency === 'EUR' ? '0,00 €' : '0,000 TND'}</strong>
                </div>
                <div className="payment-summary-total">
                  <span>{t('payment.total')}</span>
                  <div style={{ textAlign: 'right' }}>
                    <span>{formattedTotal}</span>
                    <span style={{ display: 'block', fontSize: '0.72rem', fontWeight: 400, color: '#94a3b8' }}>{t('payment.allFeesNote')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}
